import type { AxiosResponse } from 'axios'
import type { CustomBoolean } from 'constants/enums'
import { env } from 'constants/env'
import type { DraftProps } from 'container/Home/components/Banners/interface'
import type { ViewByType } from 'container/ManageOrders'
import type {
  BindListResponse,
  BindOvBody,
  CancelOvsBody,
  CheckOvBindBody,
  OVDetalheResponse,
  OvMaintenanceBody,
  RequestOvSap,
  ResponseOvSap,
  SendOvsBody,
} from 'store/OVContext/ovContextTypes'

import api from '../api'
import type {
  DifferentiatedNegotiationParams,
  DifferentiatedNegotiationResponse,
  getMatarialsByDescriptionQueryParams,
  OVsByFilters,
  OvDetailedInfo,
  OvInvoice,
  OvMaterialsFilter,
  OvMaterialsSearchResponse,
  OvObsMtoInterface,
  OvTracking,
  ovPartner,
  QueryParamsGerenciarOVListaVddr,
  QueryParamsListarMateriaisByOv,
  QueryParamsListarTracking,
  QueryParamsOVsByFilters,
  RequestOVsByFilters,
  ReturnDetalhesND,
} from './interface'

const urlsGerenciar = {
  orders: 'ListarPedidosPorUsuario',
  ovs: 'ListarOrdensVendas',
}
export interface BodyRequest {
  codigoCor?: string
  codigoSapCliente?: string[]
  codigoTipoOrdem?: string
  codigoZona?: never
  codigosCentros?: never
  codigosMateriais?: never
  codigosStatusEstoque?: never
  codigosStatusOrdemVenda?: number[]
  codigosStatusPedido?: number[]
  dataFinal?: string
  dataInicial?: string
  documentoFatura?: string
  escritorioVenda?: string
  hierarquia?: string
  materialMTO?: boolean
  numeroNotaFiscal?: number[]
  numeroOrdemVenda?: string[]
  numeroOrdemVendaSAP?: string[]
  numeroPedidoCliente?: string[]
  numeroPedidoRascunho?: string[]
  searchBox?: string
  page?: number
  flagPedidoAntecipado?: CustomBoolean
}

export const getGerenciarOVListaVddr = async (
  collection: Exclude<ViewByType, 'orderAnticipation'>,
  query: QueryParamsGerenciarOVListaVddr,
  body: BodyRequest,
  signal?: AbortSignal,
) => {
  const { Paginas, Linhas } = query
  const returnApi = api.post<DraftProps[]>(
    `${env.manageOVBasePath}/${urlsGerenciar[collection]}?Paginas=${Paginas}&Linhas=${Linhas}`,
    body,
    { signal },
  )

  return returnApi
}

export const getPartnersOV = async (ovNumber: string) => {
  const returnApi = api.get<ovPartner[]>(`${env.manageOVBasePath}/${ovNumber}/parceiros`)

  return returnApi
}

export type OvDataType = {
  Centro: string
  CodigoClienteEntrega: string
  DataDesejadaCliente: string
  DataPrevistaFaturamento: string
  DiasAdicionais: number
  NumeroPedidoCliente?: string | number
}

export const getOVsByFilters = async (
  query: QueryParamsOVsByFilters,
  body: RequestOVsByFilters,
) => {
  const { currentPage, pageSize } = query
  const returnApi = await api.post<OVsByFilters[]>(
    `${env.manageOVBasePath}/ListarOrdensVendas?Paginas=${currentPage}&Linhas=${pageSize}`,
    body,
  )

  return returnApi.data
}

export const getInvoicesOV = async (query: QueryParamsOVsByFilters, ovNumber: string) => {
  const { currentPage, pageSize } = query
  const returnApi = api.post<OvInvoice>(
    `${env.manageOVBasePath}/${ovNumber}/faturamento?paginas=${currentPage - 1}&linhas=${pageSize}`,
  )

  return returnApi
}

export const getOvInvoiceDetail = async (invoiceNumber: string, page: number, lines: number) => {
  const returnApi = api.get(
    `${env.manageOVBasePath}/faturamento/${invoiceNumber}/detalhes?paginas=${page}&linhas=${lines}`,
  )

  return returnApi
}

export const getOVMaterials = async (
  query: QueryParamsListarMateriaisByOv,
  ovNumber: string,
  selectedView: string,
  body: OvMaterialsFilter,
) => {
  const { Paginas, Linhas } = query
  const endpoint = selectedView === 'cor' ? 'materiais' : 'materiais-sku'
  const response = await api.post(
    `${env.manageOVBasePath}/${ovNumber}/${endpoint}?paginas=${Paginas}&linhas=${Linhas}`,
    body,
  )

  return response.data
}

export const getNDDetails = async (query: QueryParamsListarMateriaisByOv, ovNumber: string) => {
  const { Paginas, Linhas } = query
  const returnApi = api.post<ReturnDetalhesND>(
    `${env.manageOVBasePath}/${ovNumber}/detalhes-materiais?paginas=${Paginas}&linhas=${Linhas}`,
  )

  return returnApi
}

export const getObservacaoOrdemVenda = async (invoiceNumber: string) => {
  const returnApi = api.get<OvObsMtoInterface>(
    `${env.manageOVBasePath}/${invoiceNumber}/observacao`,
  )

  return returnApi
}

export const downloadAnexoObservacao = async (invoiceNumber: string, fileName: string) => {
  const returnApi = api.get<Blob>(
    `${env.manageOVBasePath}/DownloadAnexoObservacao?numeroOrdemVenda=${invoiceNumber}&nomeArquivo=${fileName}`,
    { responseType: 'blob' },
  )

  return returnApi
}

export const getOVTracking = async (query: QueryParamsListarTracking, ovNumber: string) => {
  const { Paginas, Linhas } = query
  const response = await api.get<OvTracking[]>(
    `${env.manageOVBasePath}/${ovNumber}/tracking?paginas=${Paginas}&linhas=${Linhas}`,
  )

  return response.data
}

export const setOvSap = async ({ type, body }: RequestOvSap) => {
  const response = await api.post<Array<ResponseOvSap>>(`${env.manageOVBasePath}/sap/${type}`, body)
  return response
}

export const sendOvSap = async (body: SendOvsBody) => {
  const response = await api.post<Array<ResponseOvSap>>(`${env.manageOVBasePath}/sap/enviar`, body)
  return response
}

export const getOvDetails = async (ovNumber: string) => {
  const response = await api.get<Omit<OVDetalheResponse, 'Financeiro'>>(
    `${env.manageOVBasePath}/${ovNumber}/${env.ovDetailBasePath}`,
  )
  return response
}

export const getMatarialsByDescription = async ({
  inputValue,
  linhas,
  ovNumber,
  paginas,
}: getMatarialsByDescriptionQueryParams) => {
  const response = await api.get<OvMaterialsSearchResponse>(
    `produto/v2/ListarMaterialByDescricao?nr_orde_vend=${ovNumber}&ds_matr=${inputValue}&paginas=${paginas}&linhas=${linhas}`,
  )
  return response.data
}

export const getOvTypes = async () => {
  const response = await api.get(
    `${env.commercialStructure}/${env.apiVersion}/ListaTipoOV?tp_orde=V`,
  )
  return response
}

export const getDifferentiatedNegotiation = async (ovNumber: string) => {
  const response = await api.get<DifferentiatedNegotiationResponse[]>(
    `${env.manageOVBasePath}/${ovNumber}/negociacao-diferenciada`,
  )

  return response.data
}

export const insertDifferentiatedNegotiation = async (body: DifferentiatedNegotiationParams) => {
  const response = await api.post(
    `${env.manageOVBasePath}/InserirJustificativaNegociacaoDiferenciada`,
    body,
  )

  return response.data
}

export const oVMaintenance = async (ovNumber: string, body: OvMaintenanceBody) => {
  const response = await api.post(
    `${env.manageOVBasePath}/${ovNumber}/${env.maintenanceOVBasePath}`,
    body,
  )
  return response.data
}

export const getOrderReport = async (orderNumber: string) => {
  const response: AxiosResponse<ArrayBuffer> = await api.post(
    `${env.manageOVBasePath}/${orderNumber}/exportar-relatorio-saldo-critica`,
    {},
    { responseType: 'arraybuffer' },
  )
  return response.data
}

export const downloadManagementReport = async (payload: RequestOVsByFilters) => {
  const reportEndpoint = 'exportar-relatorio-gerenciamento'

  const response: AxiosResponse<ArrayBuffer> = await api.post(
    `${env.manageOVBasePath}/${reportEndpoint}`,
    payload,
    { responseType: 'arraybuffer' },
  )

  return response.data
}

export const getOvDetailedInfo = async (ovNumber: string) => {
  const response = await api.get<OvDetailedInfo>(
    `${env.manageOVBasePath}/${ovNumber}/${env.ovDetailedInfo}`,
  )

  return response.data
}

export const deleteOvs = async (ovs: string[]) => {
  const response = await api.delete(`${env.manageOVBasePath}/ordens-venda`, { data: ovs })

  return response.data
}

export const cancelOvs = async (body: CancelOvsBody) => {
  const response = await api.put(`${env.manageOVBasePath}/ordens-venda/cancelar`, body)

  return response.data
}

export const checkOvBind = async (body: CheckOvBindBody) => {
  const response = await api.get<boolean>(
    `${env.manageOVBasePath}/referencia-ov-bonificada/validar?numeroOrdemVenda=${body.numeroOrdemVenda}&numeroOrdemVendaRefe=${body.numeroOrdemVendaRefe}`,
  )

  return response.data
}

export const listOvBinds = async (ovNumber: string) => {
  const response = await api.get<BindListResponse>(
    `${env.manageOVBasePath}/referencia-ov-bonificada/listar?numeroOrdemVenda=${ovNumber}`,
  )

  return response.data
}

export const manageOvBind = async (body: BindOvBody) => {
  const response = await api.post(
    `${env.manageOVBasePath}/referencia-ov-bonificada/manutencao`,
    body,
  )

  return response.data
}
